import React, { CSSProperties, forwardRef } from "react";

import { EntityType } from "@mapmycustomers/shared";

import TimelineAction from "@app/enum/TimelineAction";
import TimelineEvent, {
  TimelineEventActivityAssigned,
  TimelineEventActivityCompleted,
  TimelineEventFileAddedInActivity,
  TimelineEventFileAddedInRecord,
  TimelineEventNewActivity,
  TimelineEventNewCompany,
  TimelineEventNewDeal,
  TimelineEventNewPerson,
  TimelineEventNoteAdded,
  TimelineEventRouteCreated,
  TimelineEventUpdatedDeal,
  TimelineEventUserMentionedInActivityNote,
  TimelineEventUserMentionedInRecordNote,
} from "@app/types/events/TimelineEvent";
import loggingService from "@app/util/logging";

import ActivityAssignedEvent from "./ActivityAssignedEvent";
import ActivityCompletedEvent from "./ActivityCompletedEvent";
import ActivityScheduledEvent from "./ActivityScheduledEvent";
import CheckInEvent from "./CheckInEvent";
import FileAddedEvent from "./FileAddedEvent";
import NewCompanyOrPersonEvent from "./NewCompanyOrPersonEvent";
import NewDealEvent from "./NewDealEvent";
import NoteAddedEvent from "./NoteAddedEvent";
import RouteCreatedEvent from "./RouteCreatedEvent";
import StageChangedEvent from "./StageChangedEvent";
import UserMentionedEvent from "./UserMentionedEvent";

export interface EventItemProps {
  event: TimelineEvent;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

const EventItem = forwardRef<HTMLDivElement, EventItemProps>(
  ({ event, expanded, onSizeChange, style }, ref) => {
    switch (event.action) {
      case TimelineAction.RECORD_ADDED:
        switch (event.entity.entity) {
          case EntityType.COMPANY:
          case EntityType.PERSON:
            return event.entity ? (
              <NewCompanyOrPersonEvent
                event={event as TimelineEventNewCompany | TimelineEventNewPerson}
                expanded={expanded}
                onSizeChange={onSizeChange}
                ref={ref}
                style={style}
              />
            ) : null;
          case EntityType.DEAL:
            return event.entity ? (
              <NewDealEvent
                event={event as TimelineEventNewDeal}
                expanded={expanded}
                onSizeChange={onSizeChange}
                ref={ref}
                style={style}
              />
            ) : null;
          case EntityType.ACTIVITY:
            return event.entity ? (
              <ActivityScheduledEvent
                event={event as TimelineEventNewActivity}
                expanded={expanded}
                onSizeChange={onSizeChange}
                ref={ref}
                style={style}
              />
            ) : null;
          default:
            loggingService.warning(`Unhandled event, action=${event.action}`, { event });
            return null;
        }
      case TimelineAction.RECORD_UPDATED: {
        const record = event.entity;
        if (record?.entity === EntityType.DEAL && event.updated?.some((item) => item.dealStageId)) {
          return (
            <StageChangedEvent
              event={event as TimelineEventUpdatedDeal}
              expanded={expanded}
              onSizeChange={onSizeChange}
              ref={ref}
              style={style}
            />
          );
        }
        return null;
      }

      case TimelineAction.MENTION:
        return event.entity ? (
          <UserMentionedEvent
            event={
              event as
                | TimelineEventUserMentionedInActivityNote
                | TimelineEventUserMentionedInRecordNote
            }
            expanded={expanded}
            onSizeChange={onSizeChange}
            ref={ref}
            style={style}
          />
        ) : null;
      case TimelineAction.NOTE_ADDED:
        return event.entity ? (
          <NoteAddedEvent
            event={event as TimelineEventNoteAdded}
            expanded={expanded}
            onSizeChange={onSizeChange}
            ref={ref}
            style={style}
          />
        ) : null;
      case TimelineAction.ACTIVITY_COMPLETED:
        return event.entity.reliability !== null ? (
          <CheckInEvent
            event={event as TimelineEventActivityCompleted}
            expanded={expanded}
            onSizeChange={onSizeChange}
            ref={ref}
            style={style}
          />
        ) : (
          <ActivityCompletedEvent
            event={event as TimelineEventActivityCompleted}
            expanded={expanded}
            onSizeChange={onSizeChange}
            ref={ref}
            style={style}
          />
        );
      case TimelineAction.ROUTE_CREATED:
        return (
          <RouteCreatedEvent
            event={event as TimelineEventRouteCreated}
            expanded={expanded}
            onSizeChange={onSizeChange}
            ref={ref}
            style={style}
          />
        );
      case TimelineAction.ACTIVITY_ASSIGNED: {
        return (
          <ActivityAssignedEvent
            event={event as TimelineEventActivityAssigned}
            expanded={expanded}
            onSizeChange={onSizeChange}
            ref={ref}
            style={style}
          />
        );
      }
      case TimelineAction.FILE_ADDED:
        return (
          <FileAddedEvent
            event={event as TimelineEventFileAddedInActivity | TimelineEventFileAddedInRecord}
            expanded={expanded}
            onSizeChange={onSizeChange}
            ref={ref}
            style={style}
          />
        );
    }
    // Ignore the warning, we know some events can still slip though
    // @ts-ignore
    loggingService.warning(`Unhandled event, action=${event.action}`, { event });
    return null;
  }
);

export default EventItem;
